import _slicedToArray from "C:\\a\\2\\s\\Programs\\Microservices.Common.Frontend\\ecrion-components\\node_modules\\babel-preset-react-app\\node_modules\\@babel\\runtime/helpers/esm/slicedToArray";
import _objectSpread from "C:\\a\\2\\s\\Programs\\Microservices.Common.Frontend\\ecrion-components\\node_modules\\babel-preset-react-app\\node_modules\\@babel\\runtime/helpers/esm/objectSpread"; //@ts-check

import React, { useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import Loading from '../../../../core/Loading';
import Icon from '../../../../core/Icon';
import Page from '../../../../page';
import Utils from '../../../../utils';
import DocumentWriter from '../../../DocumentWriter';
import apiClient from '../data';
import utils from '../data/utils';
import Toolbar from './Toolbar';
import BlockPrompt from '../../../../form/BlockPrompt';
import useRun from '../context/useRun';
import RunFormFrame from './CommunicationRun/RunFormFrame';
import { DEFAULT_STATUS_VALUES } from '../../../../formatter/job/JobStatus';
import EmbedToolbar from './EmbedToolbar';
import jobNameProvider from '../logic/jobNameProvider';
import formHeightChangeNotifier from '../logic/formHeightChangeNotifier';

var COMMUNICATION_STATUS_VALUES = _objectSpread({}, DEFAULT_STATUS_VALUES, {
  WaitingForInput: 'Draft',
  Starting: 'In Progress',
  WaitingForSignature: 'In Progress'
});

var getStatus = function getStatus(job) {
  if (!job) return 'New';
  return COMMUNICATION_STATUS_VALUES[job.Stage] || 'Draft';
};

var isJobFinished = function isJobFinished(job) {
  if (!job) return false;
  return job.Stage === 'FinishedOK' || job.Stage === 'FinishedWithErrors' || job.Stage === 'Aborted';
};

var CommunicationRun = function CommunicationRun(props) {
  var blockPromptRef = React.useRef(null);
  var poolingJobTimeout = React.useRef(null);
  var refreshPreviewTimeout = React.useRef(null);
  var autosaveIntervalFn = React.useRef(null);
  var formLoaded = React.useRef(false);
  var formHeight = React.useRef(null);
  /** @type {React.Ref<import('../../EcrionForms/api/types').EcrionFormApi>} */

  var formApiRef = React.useRef(null);
  var trackingData = React.useRef({
    anonymousTrackerId: '',
    anonymousUserId: ''
  });
  var toolbarProps = props.toolbarProps,
      toolbar = props.toolbar,
      disableJobSaving = props.disableJobSaving;
  var hideNavigationButtons = props.hideNavigationButtons,
      hideFinalizeToolbar = props.hideFinalizeToolbar,
      disableRepositoryAccess = props.disableRepositoryAccess;
  var job = props.job,
      jobId = props.jobId;
  var project = props.project,
      contactId = props.contactId,
      enableAdvanced = props.enableAdvanced;
  var submission = props.submission;
  var onBeforeSubmit = props.onBeforeSubmit,
      onJobLoaded = props.onJobLoaded;
  var onJobCreated = props.onJobCreated,
      onJobStarted = props.onJobStarted,
      onJobUpdated = props.onJobUpdated;
  var onJobProgress = props.onJobProgress,
      onJobFinished = props.onJobFinished,
      onJobDeleted = props.onJobDeleted;

  var setPristine = function setPristine() {
    formApiRef.current.setPristine(); // use this imperative API because the declarative implementation of 'BlockPrompt' component
    // is not working as expected when leave the page faster than the next render cycle so we must do this by hand

    blockPromptRef && blockPromptRef.current && blockPromptRef.current.stopBlockingHistory();
  };

  var onJobDeletedCallback = function onJobDeletedCallback() {
    setPristine(); // to avoid leave message in case we are navigating away

    onJobDeleted && onJobDeleted();
  };

  var workspace = project.Workspace,
      workflowPath = project.WorkflowPath;
  var parameters = utils.createRunParameters(contactId, props.parameters);
  var context = utils.createFormContext(props.context, parameters);
  var runOptions = utils.createRunOptions(props.enableAdvanced, parameters, onJobDeletedCallback, onJobUpdated); // enhance context with workflow name

  var workflowName = Utils.path.getFileCaption(workflowPath);
  if (workflowName) context['WorkflowName'] = workflowName;
  var runApi = useRun(project, job, runOptions, parameters, formApiRef);

  var _useState = useState({
    submitting: false,
    submitted: false,
    saving: false,
    running: false,
    error: '',
    formDirty: false
  }),
      _useState2 = _slicedToArray(_useState, 2),
      runCommState = _useState2[0],
      setCommState = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isErrorDetailsVisible = _useState4[0],
      toggleErrorDetailsVisibility = _useState4[1];

  var setRunCommState = function setRunCommState(newProps) {
    setCommState(function (prevState) {
      return _objectSpread({}, prevState, newProps);
    });
  };

  var jobFinished = isJobFinished(job);
  var runState = runApi.state,
      runActions = runApi.actions,
      documentRef = runApi.documentRef,
      _runApi$options = runApi.options,
      autosaveInterval = _runApi$options.AutosaveInterval,
      previewRefreshDelay = _runApi$options.PreviewRefreshDelay,
      manualPreviewRefresh = _runApi$options.ManualPreviewRefresh;
  var _runState$view = runState.view,
      hasDocumentView = _runState$view.hasDocumentView,
      hideDocumentView = _runState$view.hideDocumentView;
  var hasFormView = runState.view.hasFormView;
  var hideFormView = props.hideFormView || runState.view.hideFormView;
  var continueEditing = project.RunAction && project.RunAction.toLowerCase() === 'continueediting';

  var getTrackingData = function getTrackingData() {
    // check for submission first
    if (submission && submission.root) return submission.root;
    return trackingData.current;
  };

  var refreshPreview = function refreshPreview() {
    formApiRef.current.getData(getTrackingData()).then(function (formDataXml) {
      runActions.setFormData(formDataXml);
    });
  };

  var reloadRunningData = function reloadRunningData() {
    if (hasDocumentView && !hideDocumentView) refreshPreview();
  };

  useEffect(function () {
    return function () {
      // cleanup timers and intervals when component unmounted
      poolingJobTimeout.current && clearTimeout(poolingJobTimeout.current);
      refreshPreviewTimeout.current && clearTimeout(refreshPreviewTimeout.current);
      autosaveIntervalFn.current && clearInterval(autosaveIntervalFn.current);
    };
  }, []);
  var formResized = React.useCallback(function (entry) {
    formHeight.current = entry;
    formHeightChangeNotifier.emitFormHeightMessage(formLoaded.current, formHeight.current);
  }, []);
  useEffect(function () {
    if (!job) return;
    onJobLoaded && onJobLoaded(job);
    poolingForJobStatus(job);
  }, [job]);
  useEffect(function () {
    // we trigger form data changed only if the form is loaded
    if (!formLoaded.current) return; // do not refresh preview if manual refresh option is enabled

    if (manualPreviewRefresh) return;
    reloadRunningData();
  }, [hideDocumentView]);

  var setAutosaveInterval = function setAutosaveInterval(autosaveInterval) {
    if (disableJobSaving || !autosaveInterval || autosaveInterval === -1) return;
    clearInterval(autosaveIntervalFn.current);
    autosaveIntervalFn.current = setInterval(function () {
      clearInterval(autosaveIntervalFn.current);
      onSave();
    }, autosaveInterval * 1000);
  };

  var poolingForJobStatus = function poolingForJobStatus(jobStatus) {
    if (!jobStatus) return;
    var jobStage = jobStatus.Stage;
    setRunCommState({
      running: jobStage === 'Starting' || jobStage === 'Processing'
    });
    if (jobStage !== 'Processing' && jobStage !== 'Starting' && jobStage !== 'WaitingForInput' && jobStage !== 'WaitingForSignature') return onJobFinished && onJobFinished(jobStatus);
    if (jobStage === 'Processing' || jobStage === 'Starting') onJobProgress && onJobProgress(jobStatus);
    if (jobStatus.Stage === 'WaitingForInput' || jobStatus.Stage === 'WaitingForSignature') return; // no need for pooling if waiting for input

    poolingJobTimeout.current = setTimeout(function () {
      apiClient.getJobById(jobStatus.Id, workspace).then(poolingForJobStatus);
    }, 500);
  };

  var saveFormData = function saveFormData() {
    setRunCommState({
      saving: true
    });
    runActions.saveCurrentFormData(getTrackingData()).finally(function () {
      setRunCommState({
        saving: false
      });
    });
  };

  var startJob = function startJob() {
    setRunCommState({
      saving: true
    });
    var jobParameters = parameters.map(function (param) {
      return {
        Name: param.name,
        Value: param.value
      };
    });
    return formApiRef.current.getData(getTrackingData()).then(function (formXml) {
      return jobNameProvider.giveJobName(project).then(function (jobName) {
        if (jobName === null) return setRunCommState({
          saving: false
        });
        return apiClient.startJob(project.Name, jobId || null, jobName || null, formXml, true, jobParameters, isTestMode()).then(function (jobStatus) {
          setPristine();
          onJobCreated && onJobCreated(jobStatus);
          return jobStatus;
        }, function (error) {
          setRunCommState({
            error: Utils.getPrettyError(error)
          });
        }).then(function (jobStatus) {
          setRunCommState({
            saving: false
          });
          return jobStatus;
        });
      });
    });
  };

  var onSave = function onSave() {
    var startNewJob = !job || !job.Id;
    if (startNewJob) return startJob();
    return saveFormData();
  };

  var onFieldFocused = React.useCallback(function (formField) {
    return runActions.highlightField(formField);
  }, []);
  var onLoaded = React.useCallback(function () {
    reloadRunningData(); // meaning the form is loaded and ready to accept autosave

    formLoaded.current = true;

    try {
      var location = window.location.toString();
      window.parent && window.parent.postMessage(['ECR.EOS.form_finished_render', null, location], '*');
      formHeightChangeNotifier.emitFormHeightMessage(formLoaded.current, formHeight.current);
    } catch (_unused) {}
  }, [hideDocumentView, hasDocumentView]);
  var onPageChanged = React.useCallback(function (dirty, pageField) {
    // scroll the form up each time we change the page
    formHeight.current && formHeight.current.target && formHeight.current.target.parentElement && formHeight.current.target.parentElement.scrollTo({
      top: 0
    });
    if (pageField && pageField.externalId) runActions.highlightField({
      id: pageField.externalId,
      type: pageField.type,
      name: pageField.name
    });
    if (!dirty) return;

    if (!disableJobSaving && autosaveInterval && autosaveInterval != -1) {
      onSave();
      clearInterval(autosaveIntervalFn.current);
    }
  }, [job, project]);
  var onUploadFile = React.useCallback(function (file, optimizeImageWeb, widthHeightMaxLimit) {
    if (!job) {
      return startJob().then(function (job) {
        var folder = "".concat(job.OutputFolderPath, "\\Images");
        return new Promise(function (resolve) {
          Utils.apiClients.content.uploadFile(file, workspace, folder, optimizeImageWeb, widthHeightMaxLimit).then(function () {
            return resolve("".concat(folder, "\\").concat(file.name));
          });
        });
      });
    } else {
      var folder = "".concat(job.OutputFolderPath, "\\Images");
      return new Promise(function (resolve) {
        Utils.apiClients.content.uploadFile(file, workspace, folder, optimizeImageWeb, widthHeightMaxLimit).then(function () {
          return resolve("".concat(folder, "\\").concat(file.name));
        });
      });
    }
  }, [job]);
  useEffect(function () {
    var messageHandler = function messageHandler(e) {
      var message = e.data[0];
      if (message !== 'setFormData') return;
      var data = e.data[1];
      if (!trackingData.current) return;
      trackingData.current.anonymousUserId = data['Tracking.AnonymousUserId'];
      trackingData.current.anonymousTrackerId = data['Tracking.AnonymousTrackerId'];
    };

    window.addEventListener('message', messageHandler);
    window.parent && window.parent.postMessage(['getFormData', null, window.location.toString()], '*');
    return function () {
      window.removeEventListener('message', messageHandler);
    };
  }, []);

  var isTestMode = function isTestMode() {
    if (!parameters) return false;
    var testModeParam = parameters.find(function (p) {
      return p.name === 'test' || p.name === 'testMode';
    });
    return testModeParam && testModeParam.value === 'true';
  };

  var onChanged = React.useCallback(function () {
    if (!formLoaded.current) return; // do not refresh preview if manual refresh option is enabled

    if (manualPreviewRefresh) return;
    clearTimeout(refreshPreviewTimeout.current);
    refreshPreviewTimeout.current = setTimeout(function () {
      reloadRunningData();
      setAutosaveInterval(autosaveInterval);
    }, previewRefreshDelay || 1000);
  }, [hasDocumentView, hideDocumentView]);
  var onDirtyChanged = React.useCallback(function (dirty) {
    if (!formLoaded.current) return;
    setRunCommState({
      formDirty: dirty
    });
  }, [hasDocumentView, hideDocumentView]);
  var onSubmit = React.useCallback(function () {
    // give a change to it's parent to process data before
    if (onBeforeSubmit && !onBeforeSubmit()) return;
    setRunCommState({
      submitting: true
    });
    var jobParameters = parameters.map(function (param) {
      return {
        Name: param.name,
        Value: param.value
      };
    });
    var workItemPath = runOptions.workItemPath,
        workItemWorkspace = runOptions.workItemWorkspace;
    if (!project.FormPath) return jobNameProvider.giveJobName(project).then(function (jobName) {
      if (jobName === null) return setRunCommState({
        submitting: false
      });
      return apiClient.startJob(project.Name, jobId || null, jobName || null, '', false, jobParameters, isTestMode(), workItemPath, workItemWorkspace).then(function (jobStatus) {
        poolingForJobStatus(jobStatus);
        onJobCreated && onJobCreated(jobStatus);
        !continueEditing && onJobStarted && onJobStarted(jobStatus);
        setRunCommState({
          submitting: false,
          subtmitted: true
        });
      }, function (error) {
        setRunCommState({
          error: Utils.getPrettyError(error)
        });
      });
    });
    return formApiRef.current.validate().then(function (error) {
      if (error) return; // nothing to do with this error for now

      return formApiRef.current.getData(getTrackingData()).then(function (formXml) {
        if (job && job.Id) {
          return apiClient.runCommunication(project.Name, job.Id, formXml, false, isTestMode()).then(function (jobStatus) {
            setPristine();
            !continueEditing && onJobStarted && onJobStarted(jobStatus);
            continueEditing && setRunCommState({
              submitted: true
            });
            poolingForJobStatus(jobStatus);
          }, function (error) {
            setRunCommState({
              error: Utils.getPrettyError(error)
            });
          });
        } else {
          return jobNameProvider.giveJobName(project).then(function (jobName) {
            if (jobName === null) return setRunCommState({
              submitting: false
            });
            return apiClient.startJob(project.Name, jobId || null, jobName || null, formXml, false, jobParameters, isTestMode()).then(function (jobStatus) {
              setPristine();
              onJobCreated && onJobCreated(jobStatus);
              !continueEditing && onJobStarted && onJobStarted(jobStatus);
              continueEditing && setRunCommState({
                submitted: true
              });
              poolingForJobStatus(jobStatus);
            }, function (error) {
              setRunCommState({
                error: Utils.getPrettyError(error)
              });
            });
          });
        }
      });
    }).then(function () {
      setRunCommState({
        submitting: false
      });
    });
  }, [project, job]);

  var onEvent = function onEvent(action) {
    switch (action) {
      case 'Submit':
        return setRunCommState({
          submitting: true
        });

      case 'Submit.Resolved':
        setRunCommState({
          submitting: false
        });
        onJobStarted && onJobStarted();
        break;

      case 'Submit.Rejected':
        setRunCommState({
          submitting: false
        });
        break;
    }
  };

  var onZoomChanged = function onZoomChanged(zoom) {
    return runActions.setZoomTitle(zoom + '%');
  };

  var FragmentComponent = toolbar ? Page.Common : React.Fragment;
  return React.createElement(React.Fragment, null, React.createElement(BlockPrompt, {
    when: runCommState.formDirty,
    ref: blockPromptRef
  }), toolbar && React.createElement(EmbedToolbar, Object.assign({
    runApi: runApi
  }, toolbar, {
    onSubmit: (!hasFormView || hideFormView || continueEditing) && onSubmit,
    onSave: disableJobSaving && !job ? null : project.FormPath ? onSave : null,
    canSave: !job || runCommState.formDirty,
    enableAdvanced: enableAdvanced,
    onRefreshPreview: refreshPreview
  })), React.createElement(FragmentComponent, null, React.createElement("div", {
    style: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'white'
    }
  }, React.createElement(Grid, {
    container: true,
    direction: "column",
    style: {
      height: '100%'
    }
  }, !jobFinished && !toolbar && !hideFinalizeToolbar && React.createElement(Grid, {
    item: true
  }, React.createElement(Toolbar, {
    jobId: job && job.Id,
    toolbarProps: toolbarProps,
    dirty: runCommState.formDirty,
    title: getStatus(job),
    onSave: onSave,
    onSubmit: onSubmit,
    onEvent: onEvent,
    saving: runCommState.saving,
    submitting: runCommState.submitting,
    running: runCommState.running
  })), React.createElement(Grid, {
    item: true,
    xs: true,
    style: {
      position: 'relative'
    }
  }, React.createElement("div", {
    style: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    }
  }, React.createElement(Grid, {
    container: true,
    direction: "row",
    style: {
      height: '100%'
    }
  }, (hasFormView || project.FormPath) && React.createElement(RunFormFrame, {
    ref: formApiRef,
    xsSize: hideFormView ? false : !hasDocumentView || hideDocumentView ? 12 : 5,
    hidden: hideFormView,
    formResized: formResized,
    formContent: runState.form && runState.form.content,
    formData: runState.form && runState.form.data,
    options: runState.form && runState.form.options,
    context: context,
    contactId: contactId,
    formState: null,
    readonly: jobFinished || runState.form && runState.form.readonly,
    renderOptions: {
      hideNavigationButtons: hideNavigationButtons,
      hideSubmitButton: jobFinished || runState.form && runState.form.readonly,
      disableRepositoryAccess: disableRepositoryAccess,
      disableSubmitButton: runCommState.running
    },
    onChanged: onChanged,
    onDirtyChanged: onDirtyChanged,
    onSubmit: onSubmit,
    onLoaded: onLoaded,
    onUploadFile: onUploadFile,
    onFieldFocused: onFieldFocused,
    onPageChanged: onPageChanged
  }), hasDocumentView && runState.loaded && React.createElement(Grid, {
    item: true,
    xs: hideDocumentView ? false : !hasFormView || hideFormView ? 12 : 7,
    style: {
      position: 'relative',
      height: '100%',
      overflow: 'auto'
    }
  }, runState.templates && runState.templates.length > 0 && !runState.error && React.createElement(DocumentWriter, {
    ref: documentRef,
    onFieldClicked: runActions.onFieldClicked,
    onStateChanged: runActions.onStateChanged,
    updateZoom: onZoomChanged,
    workspace: project.Workspace,
    communicationName: project.Name,
    path: runState.selectedTemplate,
    focusedField: runState.selectedField,
    discussion: runState.discussion,
    input: runState.input,
    jobId: job && job.Id
  }), (!runState.templates || !runState.templates.length) && !runState.error && React.createElement(Grid, {
    item: true,
    style: {
      marginTop: 250
    }
  }, React.createElement(Typography, {
    variant: "h4",
    align: "center"
  }, "No preview available")), runState.error && React.createElement(Grid, {
    item: true,
    style: {
      marginTop: 200,
      textAlign: 'center'
    }
  }, React.createElement(Typography, {
    variant: "h5",
    gutterBottom: true
  }, "An error occurred while generating the preview.", React.createElement("br", null), "Please contact your administrator."), React.createElement(Icon, {
    type: isErrorDetailsVisible ? 'up' : 'down',
    label: isErrorDetailsVisible ? 'Hide details' : 'View details',
    labelPlacement: "start",
    onClick: function onClick() {
      return toggleErrorDetailsVisibility(function (visible) {
        return !visible;
      });
    }
  }), isErrorDetailsVisible && React.createElement(Typography, {
    variant: "h6",
    color: "error"
  }, runState.error)), React.createElement(Loading.DelayedLoading, {
    isLoading: runState.processing || runState.refreshing
  })))))))), React.createElement(Snackbar, {
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'center'
    },
    open: !!runCommState.error,
    onClose: function onClose() {
      setRunCommState({
        error: ''
      });
    },
    ContentProps: {
      'aria-describedby': 'message-id'
    },
    autoHideDuration: 3000
  }, React.createElement(SnackbarContent, {
    message: React.createElement("span", {
      id: "message-id"
    }, runCommState.error)
  })), React.createElement(Snackbar, {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'right'
    },
    open: !!runCommState.submitted,
    onClose: function onClose() {
      setRunCommState({
        submitted: false
      });
    },
    ContentProps: {
      'aria-describedby': 'submitted-message-id'
    },
    autoHideDuration: 3000
  }, React.createElement(SnackbarContent, {
    message: React.createElement("span", {
      id: "submitted-message-id"
    }, "Document has been submitted")
  })));
};

export default CommunicationRun;